import { Cached, Delete, Edit, MenuOpen, Newspaper } from '@mui/icons-material'
import { Box, Button, IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
} from 'material-react-table'
import { useConfirm } from 'material-ui-confirm'
import { FunctionComponent, useMemo, useState } from 'react'

import AuditIconButton from '../../../../base/components/Auditoria/AuditIconButton'
import SimpleMenu, { SimpleMenuItem } from '../../../../base/components/MyMenu/SimpleMenu'
import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import { PAGE_DEFAULT, PageInputProps } from '../../../../interfaces'
import { genApiQuery } from '../../../../utils/helper'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { notDanger, notSuccess } from '../../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'
import { apiLoteEliminar } from '../../api/apiLoteEliminar'
import { apiLote } from '../../api/apiLoteListado'
import { LoteProps } from '../../interfaces'
import LoteActualizarDialog from '../actualizar/LoteActualizarDialog'
import LoteRegistroDialog from '../registro/LoteRegistroDialog'
import { LoteListadoColumns } from './LoteListadoColumns'

interface OwnProps {}

type Props = OwnProps

const LoteListado: FunctionComponent<Props> = (props) => {
  const [openNuevoLote, setOpenNuevoLote] = useState<boolean>(false)
  const [selectedLote, setSelectedLote] = useState<LoteProps | null>(null)
  const [openActualizarLote, setOpenActualizarLote] = useState<boolean>(false)
  // ESTADO DATATABLE
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  // FIN ESTADO DATATABLE

  const handleRefreshTable = async () => {
    setIsRefetching(true) // Establece el estado de "isRefetching" como verdadero para mostrar el indicador de carga

    try {
      await refetch() // Realiza la recarga de los datos utilizando la función "refetch" proporcionada por react-query
    } catch (error) {
      console.error('Error al recargar los datos:', error)
    }

    setIsRefetching(false) // Establece el estado de "isRefetching" como falso para ocultar el indicador de carga
  }
  const { data, isError, isLoading, status, refetch } = useQuery({
    queryKey: [
      'loteListado',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters)
      const fetchPagination: PageInputProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiLote(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })

  const columns = useMemo(() => LoteListadoColumns, [])
  const confirm = useConfirm()

  /**
   * Eliminación de un lote
   * @param row
   */
  const handleDeleteRow = async (row: LoteProps) => {
    const { confirmed } = await confirm({
      title: 'Confirmar Eliminación',
      content: `¿Estás seguro de que deseas eliminar el registro "${row.codigoLote} - ${row.descripcion}"?`,
    })
    if (!confirmed) return
    swalLoading()
    try {
      const resp = await apiLoteEliminar(row._id)
      if (resp) {
        notSuccess('Registro eliminado correctamente')
        await refetch() // Refresh the table data after deletion
      } else {
        notDanger('Eliminación cancelada o no se pudo completar.')
      }
    } catch (error) {
      swalException(error)
      notDanger('Error al eliminar el upsert.')
    } finally {
      swalClose()
    }
  }
  return (
    <>
      <StackMenu asideSidebarFixed>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={() => setOpenNuevoLote(true)}
            startIcon={<Newspaper />}
            color={'primary'}
          >
            Nuevo Lote
          </Button>
        </StackMenuItem>
      </StackMenu>
      <MaterialReactTable
        {...(MuiTableAdvancedOptionsProps as MRT_TableOptions<LoteProps>)}
        columns={columns}
        data={data ?? []}
        initialState={{
          showColumnFilters: true,
          columnVisibility: {
            descripcion: true,
          },
        }}
        enableRowActions={true}
        manualFiltering
        manualPagination
        manualSorting
        muiToolbarAlertBannerProps={
          isError ? { color: 'error', children: 'Error loading data' } : undefined
        }
        onColumnFiltersChange={setColumnFilters}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        enableDensityToggle={false}
        enableGlobalFilter={false}
        rowCount={rowCount}
        // localization={localization}
        state={{
          isLoading,
          columnFilters,
          pagination,
          showAlertBanner: isError,
          showProgressBars: isRefetching,
          density: 'compact',
          sorting,
          rowSelection,
        }}
        muiSearchTextFieldProps={{
          variant: 'outlined',
          placeholder: 'Busqueda',
          InputLabelProps: { shrink: true },
          size: 'small',
        }}
        positionActionsColumn={'first'}
        renderRowActions={({ row }) => (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.5rem', width: 100 }}>
            <SimpleMenu
              menuButton={
                <>
                  <IconButton aria-label="menuGestionProductos">
                    <MenuOpen />
                  </IconButton>
                </>
              }
            >
              {/* <SimpleMenuItem onClick={() => handleDeleteData([row.original])}> */}
              <SimpleMenuItem
                onClick={() => {
                  setOpenActualizarLote(true)
                  setSelectedLote(row.original)
                }}
              >
                <Edit />
                Editar
              </SimpleMenuItem>

              <SimpleMenuItem
                onClick={async () => {
                  await handleDeleteRow(row.original)
                }}
                sx={{ color: (theme) => theme.palette.error.main }}
              >
                <Delete />
                Eliminar
              </SimpleMenuItem>
            </SimpleMenu>
            <AuditIconButton row={row.original} />
          </div>
        )}
        renderTopToolbarCustomActions={({ table }) => {
          return (
            <Box sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}>
              <IconButton onClick={handleRefreshTable} disabled={isRefetching}>
                <Cached />
              </IconButton>
            </Box>
          )
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        displayColumnDefOptions={{
          'mrt-row-actions': {
            muiTableHeadCellProps: {
              align: 'center',
            },
            size: 100,
          },
        }}
      />
      <LoteRegistroDialog
        id="loteRegistroDialog"
        keepMounted={false}
        open={openNuevoLote}
        onClose={(value?: LoteProps) => {
          if (value) {
            refetch().then()
          }
          setOpenNuevoLote(false)
        }}
      />
      <LoteActualizarDialog
        keepMounted={false}
        open={openActualizarLote}
        //@ts-ignore
        lote={selectedLote}
        onClose={(value?: LoteProps) => {
          if (value) {
            refetch().then()
          }
          setOpenActualizarLote(false)
        }}
      />
    </>
  )
}

export default LoteListado
